import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Table from '@vromo/ui/components/Table';
import Container from '@vromo/ui/components/Container';
import Modal from '@vromo/ui/components/Modal';
import { ProfilePlaceholder } from '@vromo/ui/components/ProfileImage';
import Button from '@vromo/ui/components/Button';
import { Tab, Tabs } from '@vromo/ui/components/Tabs';
import { styles } from './policiesRoot.style';

import { serialize } from 'serializr';

import { createViewModel } from 'mobx-utils';
import { observer } from 'mobx-react-lite';

import { useStore } from '@vromo/mobx-react';
import { Policy, PolicyRule } from '@vromo/models';

import PolicyForm from './PolicyForm';
import BlueprintsLazy from '../blueprints/Blueprints.lazy';
import { Blueprint } from '@vromo/models';
import { EMPTY_BLUEPRINT } from '@vromo/dashboard/src/blueprints/BlueprintDefaults';
import { useToasts } from '@vromo/ui/components/Toasts';
import { RootStore } from '../stores/RootStore';
import { EntityList } from '@vromo/models';
import { Group } from '@vromo/models';
import { useModals } from '@vromo/ui/components/Modals';
import { Header, InnerContainer } from './components/styled';
import { action } from 'mobx';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import Flex from '@vromo/ui/components/Flex';
import { qaLabelAutodispatcher } from '../../../../common-qa-labels/autodispatcher';
import { buildDynamicLabel } from '../../../../common-qa-labels/utils';
import Title from '@vromo/ui/components/Title';

export default observer(function PoliciesRoot() {
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const [currentTab, setCurrentTab] = useState<string>('autoDispatchers');
    const [editedPolicy, setEditedPolicy] = useState(createViewModel(new Policy()));
    const [teamOptions, setTeamOptions] = useState<
        Array<{ label: string | null; value: number | null }>
    >([]);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [blueprintEditing, setBlueprintEditing] = useState<boolean>(false);
    const [editedBlueprint, setEditedBlueprint] = useState<Blueprint>(() => EMPTY_BLUEPRINT());

    const rootStore = useStore<RootStore>('rootStore');
    const { graph, role } = rootStore.authStore;

    const showToast = useToasts();
    const modals = useModals();

    useEffect(() => {
        if (location.pathname.includes('/job-templates')) setCurrentTab('jobTemplates');
    }, [location]);

    useEffect(() => {
        graph.root
            .graph()
            .roles(role.id)
            .groups()
            .get()
            .then(({ items }: EntityList<Group>) => {
                setTeamOptions(
                    items.map((it) => {
                        return {
                            label: it.name,
                            value: it.id
                        };
                    })
                );
            });
    }, []);

    function formatPolicyRequest(policy: Policy): Policy {
        action(() => (policy.rules = null));
        const policyBody = serialize(policy);

        delete policyBody.group;
        delete policyBody.created;

        policyBody.rules.items.forEach((r: PolicyRule) => {
            r.created = null;
            r.id = null;
        });

        if (!policy.capacity) {
            policyBody.capacity = null;
        }

        if (policy?.singleOverflowDispatch) {
            policyBody.singleOverflowDispatch = true;
        }

        return policyBody;
    }

    function postPolicy(policy: Policy) {
        const isNew = policy.id == null;
        setLoading(true);
        graph.root
            .graph()
            .roles(role.id)
            .policies()
            .post([formatPolicyRequest(policy)])
            .then(({ items }: EntityList<Policy>) => {
                showToast(
                    `Successfully ${isNew ? 'added' : 'updated'} ${items[0]?.name}`,
                    'success'
                );
                setIsEditing(false);
            })
            .catch((e) => {
                console.error(e);
                showToast(
                    `Something went wrong with ${
                        isNew ? 'adding' : 'updating'
                    } this policy. Try again`,
                    'error'
                );
            })
            .finally(() => setLoading(false));
    }

    async function policyInUse(id: number) {
        return await graph.root
            .graph()
            .roles(role.id)
            .policies(id)
            .get({ sites__include: true, blueprints__include: true });
    }

    async function confirmClose() {
        const isNew = editedPolicy?.id == null;

        if (
            !isNew &&
            editedPolicy.isDirty &&
            (await modals.confirm(
                'Unsaved Changes',
                'Are you sure you want to leave without saving your changes?'
            ))
        ) {
            setIsEditing(false);
            editedPolicy.reset();
        } else if (isNew || (!isNew && !editedPolicy.isDirty)) {
            setIsEditing(false);
        }
    }

    function deletePolicy(id: number) {
        setLoading(true);
        graph.root
            .graph()
            .roles(role.id)
            .policies(id)
            .delete()
            .then((policy) => {
                showToast(`Removed ${policy.name}`);
                setIsEditing(false);
            })
            .finally(() => setLoading(false));
    }

    const handleTabChange = (tab: string) => {
        setCurrentTab(tab);

        if (tab === 'jobTemplates') history.push('/job-templates');
        if (tab === 'autoDispatchers') history.push('/policies');
    };

    const handleNewBlueprintOnClick = (blueprint: Blueprint) => {
        setBlueprintEditing(true);
        setEditedBlueprint(blueprint);
    };

    const handleBlueprintClose = () => {
        setBlueprintEditing(false);
    };

    return (
        <>
            <Header>
                <Title>{t('policies.title')}</Title>
            </Header>
            <Flex horizontal="between" vertical="middle" style={styles.tabsContainer}>
                <Tabs currentTab={currentTab} onChange={handleTabChange}>
                    <Tab id="autoDispatchers">{t('policies.autoDispatchers')}</Tab>
                    <Tab id="jobTemplates">{t('policies.jobTemplates')}</Tab>
                </Tabs>
                {currentTab === 'autoDispatchers' && (
                    <Button
                        data-testid={qaLabelAutodispatcher.addAutodispatcherBtn}
                        onClick={() => {
                            setEditedPolicy(createViewModel(new Policy()));
                            setIsEditing(true);
                        }}>
                        {t('policies.add')}
                    </Button>
                )}
                {currentTab === 'jobTemplates' && (
                    <Button onClick={() => handleNewBlueprintOnClick(EMPTY_BLUEPRINT())}>
                        {t('blueprint.addJobTemplate')}
                    </Button>
                )}
            </Flex>
            <Container>
                <InnerContainer>
                    {currentTab === 'autoDispatchers' &&
                        (rootStore.policyStore.loaded ? (
                            <Table>
                                <thead>
                                    <tr>
                                        <th />
                                        <th>{t('policies.name')}</th>
                                        <th>{t('policies.id')}</th>
                                        <th>{t('policies.rules')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rootStore.policyStore.policies
                                        .sortBy((p: Policy) => p.name?.trim().toLowerCase())
                                        .map((policy: Policy) => {
                                            return (
                                                <tr
                                                    data-testid={buildDynamicLabel(
                                                        qaLabelAutodispatcher.autodispatcherRow,
                                                        `${policy.name}`
                                                    )}
                                                    key={policy.id}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setIsEditing(true);
                                                        setEditedPolicy(createViewModel(policy));
                                                    }}>
                                                    <td style={{ textAlign: 'center', width: 50 }}>
                                                        <ProfilePlaceholder
                                                            initials={policy.name?.[0] || ''}
                                                            id={policy.id ?? 0}
                                                        />
                                                    </td>
                                                    <td>{policy.name}</td>
                                                    <td>{policy.id}</td>
                                                    <td>{policy?.rules?.length}</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </Table>
                        ) : (
                            <Flex>
                                {t('policies.loading')} <LoadingIndicator />
                            </Flex>
                        ))}
                </InnerContainer>
                {currentTab === 'jobTemplates' && (
                    <BlueprintsLazy
                        blueprintEditing={blueprintEditing}
                        editedBlueprint={editedBlueprint}
                        blueprintOnClick={handleNewBlueprintOnClick}
                        blueprintOnClickClose={handleBlueprintClose}
                    />
                )}
                <Modal isOpen={isEditing} onRequestClose={confirmClose} allowEvents>
                    <PolicyForm
                        deletePolicy={deletePolicy}
                        loading={loading}
                        onCancel={() => setIsEditing(false)}
                        policy={editedPolicy}
                        policyInUse={policyInUse}
                        savePolicy={(p) => postPolicy(p)}
                        teamOptions={teamOptions}
                    />
                </Modal>
            </Container>
        </>
    );
});
